<template>
    <div class="sf-chat">
        <div ref="stream" class="sf-chat__stream" aria-live="polite" aria-label="Gesprek">
            <template v-for="(message, idx) in messages">
                <bot-bubble
                    v-if="message.type === 'bot'"
                    :key="'bot-' + idx"
                    :text="message.text"
                    :question="message.question"
                />
                <user-bubble
                    v-else
                    :key="'user-' + idx"
                    :text="message.text"
                />
            </template>

            <div v-if="isLoading" class="sf-chat__typing" aria-label="Aan het typen…">
                <span></span><span></span><span></span>
            </div>
        </div>

        <div v-if="currentQuestion" class="sf-chat__input-zone">
            <question-open
                v-if="currentQuestion.type === 'open'"
                :question="currentQuestion"
                @answer="forwardAnswer"
            />
            <question-single
                v-else-if="currentQuestion.type === 'single_select' || currentQuestion.type === 'taxonomy_select'"
                :question="currentQuestion"
                @answer="forwardAnswer"
            />
            <question-multi
                v-else-if="currentQuestion.type === 'multi_select'"
                :question="currentQuestion"
                @answer="forwardAnswer"
            />
            <question-date
                v-else-if="currentQuestion.type === 'date' || currentQuestion.type === 'datepicker'"
                :question="currentQuestion"
                :duration="duration"
                @answer="forwardAnswer"
            />
        </div>
    </div>
</template>

<script>
import BotBubble from './BotBubble.vue';
import UserBubble from './UserBubble.vue';
import QuestionOpen from './question/QuestionOpen.vue';
import QuestionSingle from './question/QuestionSingle.vue';
import QuestionMulti from './question/QuestionMulti.vue';
import QuestionDate from './question/QuestionDate.vue';

export default {
    name: 'ChatStream',

    components: {
        BotBubble,
        UserBubble,
        QuestionOpen,
        QuestionSingle,
        QuestionMulti,
        QuestionDate,
    },

    props: {
        messages: {type: Array, required: true},
        isLoading: {type: Boolean, default: false},
        state: {type: Object, default: () => ({})},
    },

    emits: ['answer'],

    computed: {
        duration() {
            return (this.state && this.state.filled && this.state.filled.duration) || 'day';
        },

        currentQuestion() {
            if (this.isLoading) return null;
            for (let i = this.messages.length - 1; i >= 0; i--) {
                const message = this.messages[i];
                if (message.type === 'bot' && message.question) return message.question;
            }
            return null;
        },
    },

    watch: {
        messages() {
            this.$nextTick(this.scrollToBottom);
        },
        isLoading() {
            this.$nextTick(this.scrollToBottom);
        },
    },

    methods: {
        scrollToBottom() {
            const el = this.$refs.stream;
            if (el) el.scrollTop = el.scrollHeight;
        },

        forwardAnswer(payload) {
            this.$emit('answer', payload);
        },
    },
};
</script>

<style lang="scss" scoped>
.sf-chat {
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow: hidden;
    background-color: #f4ecdf;
    border-radius: 20px;
    padding-bottom: 1rem;

    &__stream {
        flex: 1;
        overflow-y: auto;
        padding: 16px;
        display: flex;
        flex-direction: column;
        gap: 12px;
        scroll-behavior: smooth;
    }

    &__input-zone {
        border-top: 1px solid rgba(0, 0, 0, 0.06);
        padding: 8px 0 0;
        background: #f4ecdf;
    }

    &__typing {
        display: flex;
        gap: 5px;
        padding: 12px 16px;
        background: #f4ecdf;
        border-radius: 0 10px 10px 10px;
        align-self: flex-start;
        width: 52px;

        span {
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background: #b5afa5;
            animation: sf-typing 1.2s infinite;

            &:nth-child(2) {
                animation-delay: 0.2s;
            }

            &:nth-child(3) {
                animation-delay: 0.4s;
            }
        }
    }
}

@keyframes sf-typing {
    0%, 60%, 100% {
        transform: translateY(0);
        opacity: 0.4;
    }
    30% {
        transform: translateY(-5px);
        opacity: 1;
    }
}
</style>
